import { useEffect, useState } from 'react'
import { KeyRound } from 'lucide-react'
import { getThisPollOauthGrants, type ThisPollOauthGrant } from '../lib/api'

// DW-ITEM21-THIS-POLL-OAUTH-GRANT-FE — the only window onto this-poll
// oauth_grant_new_surfaced rows. Mounted on the analyst Alerts page
// (audience 'analyst', the default) and on the client home (audience
// 'client'), next to ThisPollForwardEvidence. The audience switches ONLY
// the disarmed-copy sentence — headings, aria-labels, blind-on-empty and
// blind-on-failure behaviour are identical on both surfaces.
//
// Doctrine (mirrors the backend endpoint's contract):
// - Only rows from GET /alerts/this-poll/oauth-grants appear here.
//   Historical store rows (aged grant states, lifetime counts) never do.
// - [] means BLIND: render NOTHING. No empty success card, no "no new
//   consents", no all-clear copy — absence of evidence is not a verdict,
//   and this section must not paint one. A failed fetch also renders
//   nothing: with no rows in hand the section has no claim to make in
//   either direction.
// - Response is DISARMED: app id/name + scopes are evidence for the
//   analyst's eyes. No Approve/Reject hangs off this surface. Publisher
//   unknown stays unknown; no risk score.
export function ThisPollOauthGrantEvidence({
  audience = 'analyst',
}: {
  audience?: 'analyst' | 'client'
}) {
  const [rows, setRows] = useState<ThisPollOauthGrant[]>([])

  useEffect(() => {
    let cancelled = false
    getThisPollOauthGrants()
      .then((list) => {
        if (!cancelled) setRows(list)
      })
      .catch(() => {
        // Stay BLIND: no rows, no claims, no section.
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (rows.length === 0) return null

  return (
    <section
      aria-label="This-poll OAuth consent grants"
      className="mb-6 bg-white rounded-xl border border-warning/40 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="w-4 h-4 text-warning" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-navy">
          OAuth consent grants surfaced this poll
        </h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Evidence from the current poll only — not a verdict.{' '}
        {audience === 'client'
          ? 'The team decides next steps. This surface executes nothing.'
          : 'The analyst makes the call; nothing executes from this section.'}
      </p>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li
            key={row.signal_id}
            className="rounded-lg border border-gray-200 p-3 text-sm text-navy"
          >
            <ul className="space-y-2">
              {(row.evidence ?? []).map((item, i) => (
                <li key={`${row.signal_id}-${item.app_id ?? i}`}>
                  <span className="font-medium">
                    {item.app_name ?? '(app name not on payload)'}
                  </span>
                  <span className="text-gray-500">
                    {' '}
                    ({item.app_id ?? 'app id unknown'})
                  </span>
                  <span className="text-gray-500"> — scopes: </span>
                  <span>
                    {(item.scopes ?? []).filter(Boolean).join(', ') ||
                      '(scopes not on payload)'}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  )
}
